import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NotyfContext from "../../../../contexts/NotyfContext";

import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";

// Icons
import Icon from "@mdi/react";
import { mdiLoading, mdiCalendarAlert, mdiOpenInNew } from "@mdi/js";
import { MdAdd } from "react-icons/md";

// Components
import Reports from "./components/tables/Reports";

// Redux
import {
  createReport,
  showReport,
} from "../../../../redux/slices/reports/projectReportSlice";

const Reporting = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const notyf = useContext(NotyfContext);

  // Scopes still missing Tarikh Jangka Mula / Siap — reporting is blocked
  // until every scope has both dates.
  const [missingScopes, setMissingScopes] = useState([]);
  const [showNewReportBtn, setShowNewReportBtn] = useState(false);
  const error = missingScopes.length > 0;

  const { projectId } = routeParams;
  const { report, btnLoading, project } = useSelector((state) => ({
    report: state.reportReducer.report,
    btnLoading: state.reportReducer.btnLoading,
    project: state.reportReducer.project,
  }));

  const handleMakeReport = async () => {
    const newValues = {
      project_id: projectId,
    };
    await dispatch(createReport(newValues)).then(({ payload }) => {
      const { id } = payload;
      navigate(`/laporan/kemaskini-kemajuan-projek/${projectId}/laporan/${id}`);
    });
  };

  const handleItemReport = (item) => {
    if (error) {
      handleNotification();
    } else {
      const { id } = item;
      navigate(`/laporan/kemaskini-kemajuan-projek/${projectId}/laporan/${id}`);
    }
  };

  // Open the project profile with the Skop Projek section already expanded
  // and scrolled into view, so the user lands on the fields to fill.
  const handleGoToProfileProject = () => {
    navigate(`/profil-projek/${project.uuid}`, { state: { openSection: "2" } });
  };

  // Notification
  const handleNotification = () => {
    let msg = `Lengkapkan Tarikh Jangka Mula dan Tarikh Jangka Siap bagi setiap skop projek dahulu.`;
    let color = "error";

    // Success
    notyf.open({
      type: color,
      message: msg,
      duration: 5000,
      ripple: false,
      dismissible: true,
      position: {
        x: "right",
        y: "top",
      },
    });
  };

  useEffect(() => {
    const isCompleted = project?.progress_status === 8;

    if (isCompleted) {
      setShowNewReportBtn(false);
    } else {
      setShowNewReportBtn(true);
    }

    // A scope counts as incomplete when its detail row is missing or either
    // date is empty.
    const missing = (project.financial_details ?? []).filter((item) => {
      const detail = item.project_scope_details;
      return !detail || !detail.expected_start_date || !detail.expected_completion_date;
    });
    setMissingScopes(missing);
  }, [project]);

  const totalScopes = project.financial_details?.length ?? 0;

  return (
    <React.Fragment>
      <Card>
        <Card.Header className='pb-0'>
          <Table className='mb-0' size='sm' borderless>
            <tbody>
              <tr>
                <td style={{ width: 120, fontWeight: 700 }}>Program</td>
                <td>{project.program?.label}</td>
              </tr>
              <tr>
                <td style={{ width: 120, fontWeight: 700 }}>Projek Utama</td>
                <td>
                  {project.main_project?.code} {project.main_project?.label}
                </td>
              </tr>
              <tr>
                <td style={{ width: 120, fontWeight: 700 }}>Nama Projek</td>
                <td>
                  {project.code} {project?.name}
                </td>
              </tr>
            </tbody>
          </Table>
          {showNewReportBtn && (
            <div className='card-actions float-end'>
              <Tooltip
                placement='left'
                title={
                  error
                    ? "Lengkapkan Tarikh Jangka Mula dan Siap bagi setiap skop projek dahulu"
                    : "Buat Laporan Baharu"
                }
                arrow
              >
                <span>
                  <Button
                    onClick={handleMakeReport}
                    variant='secondary'
                    disabled={btnLoading || (error ? true : false)}
                  >
                    {btnLoading ? (
                      <Icon
                        className='mx-4'
                        path={mdiLoading}
                        size={0.8}
                        spin={true}
                      />
                    ) : (
                      <span>
                        <MdAdd /> Laporan
                      </span>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </Card.Header>
        <Card.Body>
          {error && (
            <div
              className='d-flex align-items-start gap-3 p-3 rounded shadow-sm mb-3'
              style={{ backgroundColor: "#fff8e6", borderLeft: "4px solid #f0a500" }}
            >
              <div
                className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                style={{ width: 44, height: 44, backgroundColor: "rgba(240, 165, 0, 0.18)" }}
              >
                <Icon path={mdiCalendarAlert} size={1} color='#c58900' />
              </div>
              <div className='flex-grow-1' style={{ minWidth: 0 }}>
                <div className='d-flex align-items-center gap-2 flex-wrap'>
                  <span className='fw-bolder' style={{ color: "#7a5600", fontSize: 15 }}>
                    Laporan kemajuan belum boleh dibuat
                  </span>
                  <span
                    className='badge fw-bold'
                    style={{ backgroundColor: "rgba(240, 165, 0, 0.25)", color: "#7a5600" }}
                  >
                    {missingScopes.length} / {totalScopes} skop belum lengkap
                  </span>
                </div>
                <div className='mt-1' style={{ color: "#5c4300" }}>
                  Setiap skop projek perlu mempunyai{" "}
                  <span style={{ fontWeight: 900 }}>Tarikh Jangka Mula</span> dan{" "}
                  <span style={{ fontWeight: 900 }}>Tarikh Jangka Siap</span> sebelum laporan
                  kemajuan boleh dibuat.
                </div>



                <div className='d-flex align-items-center gap-3 flex-wrap mt-3'>
                  <Button variant='warning' size='sm' onClick={handleGoToProfileProject}>
                    Lengkapkan tarikh di Profil Projek
                    <Icon path={mdiOpenInNew} size={0.65} className='ms-1' />
                  </Button>
                  <span style={{ fontSize: 12, color: "#8a6d1a" }}>
                    Anda akan dibawa terus ke seksyen Skop Projek. Butang{" "}
                    <b>+ Laporan</b> akan diaktifkan semula selepas semua tarikh diisi.
                  </span>
                </div>
              </div>
            </div>
          )}
          <Reports handleItemReport={handleItemReport} />
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default Reporting;
